import { MovementDataPoint, MovementMetric, MovementMetricType } from '../interfaces/types';

// Generates realistic movement data with subtle trends over time
export const generateMovementData = (
  startDate: Date = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), // 90 days ago
  endDate: Date = new Date(), 
  pointsPerDay: number = 4,
  includeSubtleChanges: boolean = true
): MovementDataPoint[] => {
  const dataPoints: MovementDataPoint[] = [];
  const daySpan = Math.floor((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
  
  // Baseline values for each metric
  const baselineValues = {
    stepLength: 65, // cm
    stepSymmetry: 0.95, // 1.0 = perfect symmetry
    walkingSpeed: 3.9, // ft/s
    cadence: 110, // steps per minute
    sitToStandTime: 2.5, // seconds
    balanceScore: 88, // 0-100
    dailyStepCount: 4200, // steps
    variabilityIndex: 0.12, // lower = more consistent
    turnAngle: 35, // degrees
    posturalSway: 22, // mm
  };
  
  // Subtle degradation factors (per day)
  const degradationFactors = includeSubtleChanges ? {
    stepLength: -0.03, // subtle decrease in step length
    stepSymmetry: -0.0004, // very subtle decrease in symmetry
    walkingSpeed: -0.007, // subtle slowing
    cadence: -0.02, // subtle decrease in cadence
    sitToStandTime: 0.005, // gradual increase in time to stand
    balanceScore: -0.04, // gradual decrease in balance
    dailyStepCount: -2.5, // decrease in daily activity
    variabilityIndex: 0.0005, // increased variability over time
    turnAngle: -0.01, // gradually decreasing turn angle
    posturalSway: 0.04, // increasing sway
  } : {
    stepLength: 0,
    stepSymmetry: 0,
    walkingSpeed: 0,
    cadence: 0,
    sitToStandTime: 0,
    balanceScore: 0,
    dailyStepCount: 0,
    variabilityIndex: 0,
    turnAngle: 0,
    posturalSway: 0,
  };
  
  // Random noise level for each metric to simulate natural variation
  const noiseFactors = {
    stepLength: 2.0,
    stepSymmetry: 0.02,
    walkingSpeed: 0.33,
    cadence: 5.0,
    sitToStandTime: 0.3,
    balanceScore: 3.0,
    dailyStepCount: 500,
    variabilityIndex: 0.01,
    turnAngle: 2.0,
    posturalSway: 2.0,
  };
  
  // Weekly patterns for some metrics
  const weeklyPatterns = {
    dailyStepCount: [0.9, 1.0, 1.0, 0.95, 1.0, 1.1, 0.8], // weekend lower
    balanceScore: [1.0, 1.0, 0.98, 0.99, 0.98, 1.0, 1.01], // slight weekend improvement
  };
  
  // Activity types to randomly assign
  const activityTypes = ['walking', 'sitting', 'standing', 'bathroom', 'kitchen', 'bedroom', 'exercise'];
  
  for (let day = 0; day < daySpan; day++) {
    const dayDate = new Date(startDate.getTime() + day * 24 * 60 * 60 * 1000);
    const dayOfWeek = dayDate.getDay(); // 0 = Sunday, 6 = Saturday
    
    for (let point = 0; point < pointsPerDay; point++) {
      const pointTime = new Date(dayDate.getTime() + (point * 24 * 60 * 60 * 1000) / pointsPerDay);
      
      // Calculate values with degradation, noise, and weekly patterns
      const metrics: { [key: string]: number } = {};
      
      // Add random variation and degradation to each metric
      Object.entries(baselineValues).forEach(([key, baseValue]) => {
        const degradation = (degradationFactors as any)[key] * day;
        const noise = ((Math.random() * 2) - 1) * (noiseFactors as any)[key];
        const weeklyFactor = (weeklyPatterns as any)[key] ? (weeklyPatterns as any)[key][dayOfWeek] : 1.0;
        
        metrics[key] = (baseValue + degradation + noise) * weeklyFactor;
      });
      
      // Generate random activities
      const numActivities = 1 + Math.floor(Math.random() * 3); // 1-3 activities
      const activities = Array(numActivities).fill(0).map(() => {
        const activityType = activityTypes[Math.floor(Math.random() * activityTypes.length)];
        const duration = 5 + Math.floor(Math.random() * 25); // 5-30 minutes
        return { type: activityType, duration };
      });
      
      // Create the data point
      dataPoints.push({
        timestamp: pointTime,
        metrics,
        activities
      });
    }
  }
  
  return dataPoints;
};

export const getMovementMetrics = (): MovementMetric[] => {
  return [
    {
      id: 'stepLength',
      name: 'Step Length',
      type: 'GAIT',
      description: 'Average length of steps during walking',
      value: 65,
      unit: 'cm',
      normalRange: { min: 60, max: 80 },
      timestamp: new Date()
    },
    {
      id: 'stepSymmetry',
      name: 'Step Symmetry',
      type: 'GAIT',
      description: 'Symmetry between left and right steps (1.0 = perfect)',
      value: 0.95,
      unit: 'ratio',
      normalRange: { min: 0.9, max: 1.0 },
      timestamp: new Date()
    },
    {
      id: 'walkingSpeed',
      name: 'Walking Speed',
      type: 'GAIT',
      description: 'Average speed during normal walking',
      value: 3.9,
      unit: 'ft/s',
      normalRange: { min: 3.3, max: 4.6 },
      timestamp: new Date()
    },
    {
      id: 'cadence',
      name: 'Step Cadence',
      type: 'GAIT',
      description: 'Number of steps per minute during walking',
      value: 110,
      unit: 'steps/min',
      normalRange: { min: 100, max: 130 },
      timestamp: new Date()
    },
    {
      id: 'sitToStandTime',
      name: 'Sit-to-Stand Time',
      type: 'MOBILITY',
      description: 'Time taken to transition from sitting to standing',
      value: 2.5,
      unit: 'seconds',
      normalRange: { min: 1.5, max: 4.0 },
      timestamp: new Date()
    },
    {
      id: 'balanceScore',
      name: 'Balance Score',
      type: 'BALANCE',
      description: 'Overall score for balance stability',
      value: 88,
      unit: 'score',
      normalRange: { min: 75, max: 100 },
      timestamp: new Date()
    },
    {
      id: 'dailyStepCount',
      name: 'Daily Step Count',
      type: 'ACTIVITY_LEVEL',
      description: 'Total steps taken per day',
      value: 4200,
      unit: 'steps',
      normalRange: { min: 3000, max: 7000 },
      timestamp: new Date()
    },
    {
      id: 'variabilityIndex',
      name: 'Gait Variability',
      type: 'GAIT',
      description: 'Measure of gait consistency (lower is better)',
      value: 0.12,
      unit: 'index',
      normalRange: { min: 0.05, max: 0.25 },
      timestamp: new Date()
    },
    {
      id: 'turnAngle',
      name: 'Turn Angle',
      type: 'MOBILITY',
      description: 'Average rotation angle during turning',
      value: 35,
      unit: 'degrees',
      normalRange: { min: 25, max: 45 },
      timestamp: new Date()
    },
    {
      id: 'posturalSway',
      name: 'Postural Sway',
      type: 'POSTURE',
      description: 'Amount of body sway during standing',
      value: 22,
      unit: 'mm',
      normalRange: { min: 15, max: 30 },
      timestamp: new Date()
    }
  ];
}; 